import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import PageShell from "@/components/PageShell";
import PageHeader from "@/components/PageHeader";
import TraditionTag from "@/components/TraditionTag";
import TaggedOutbound from "@/components/TaggedOutbound";
import { getMusicCatalog, getSongCount, formatDuration } from "@/lib/catalog";
import { ytThumbUrl } from "@/lib/youtube";
import { trackEvent } from "@/lib/tracking";

// /listen — the music catalogue, with somewhere to actually go.
//
// The old /music index showed a cover and a title and nothing else: every
// streaming link lived one page deeper, so the shortest path from "I want to
// hear this" to a platform was two navigations. Each of those links is now on
// the card, tagged, and firing `music_platform_click` — the event the music
// arm's whole verdict turns on and which, until today, no page in this site
// was capable of sending.
//
// /music still works and still routes here; the two names cost nothing and a
// link that 404s costs a reader.

const FILTERS = [
  { id: "all", label: "All" },
  { id: "hindu", label: "Hindu" },
  { id: "buddhist", label: "Buddhist" },
];

function platformsFor(song) {
  return [
    song.spotify_url && { id: "spotify", label: "Spotify", url: song.spotify_url },
    song.apple_music_url && { id: "apple", label: "Apple", url: song.apple_music_url },
    song.youtube_music_url && { id: "yt-music", label: "YT Music", url: song.youtube_music_url },
  ].filter(Boolean);
}

function Card({ card, index }) {
  const rep = card.representative;
  const ytId = rep?.youtube_video_id;
  const cover = card.cover_url || (ytId ? ytThumbUrl(ytId, "hqdefault") : null);
  const platforms = platformsFor(card.album_links || rep || {});

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.85,
        delay: Math.min(index * 0.04, 0.28),
        ease: [0.22, 1, 0.36, 1],
      }}
      className="flex flex-col"
    >
      <Link
        to={`/listen/${card.slug}`}
        className="block group"
        onClick={() =>
          trackEvent("product_open", {
            title_id: card.slug,
            slug: card.slug,
            surface: "listen",
            kind: "song",
          })
        }
      >
        <div className="card-shrine rounded-sm overflow-hidden">
          <div className="relative aspect-square overflow-hidden bg-parchment-deep">
            {cover ? (
              <>
                <img
                  src={cover}
                  alt={card.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-slower ease-breath group-hover:scale-[1.05]"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-shrine-shadow/60 via-transparent to-transparent" />
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-parchment via-parchment-deep to-dawn-rose/40">
                <span className="font-display text-4xl text-dharma-gold-deep/30">ॐ</span>
              </div>
            )}
            <div className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-cream/90 flex items-center justify-center backdrop-blur-sm border border-dharma-gold/30 group-hover:bg-dharma-gold transition-colors duration-700 ease-breath">
              <Play
                size={14}
                className="text-dharma-gold-deep group-hover:text-cream transition-colors duration-700 ease-breath ml-0.5"
                fill="currentColor"
              />
            </div>
          </div>

          <div className="p-4 md:p-5">
            <h3 className="font-display text-base md:text-lg text-ink leading-snug group-hover:text-dharma-gold-deep transition-colors duration-700 ease-breath">
              {card.title}
            </h3>
            <div className="flex items-center gap-3 mt-2">
              <TraditionTag tradition={card.tradition} />
              {card.is_group ? (
                <span className="font-body text-[0.65rem] tracking-widest2 uppercase text-ink-mute">
                  {card.members.length} {card.is_album ? "tracks" : "versions"}
                </span>
              ) : (
                rep?.duration_seconds && (
                  <span className="font-body text-[0.7rem] text-ink-mute">
                    {formatDuration(rep.duration_seconds)}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* Tagged streaming links, on the card. */}
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5 min-h-[1.9rem]">
        {platforms.length ? (
          platforms.map((pl) => (
            <TaggedOutbound
              key={pl.id}
              url={pl.url}
              label={pl.label}
              event="music_platform_click"
              store={pl.id}
              titleId={card.slug}
              slug={card.slug}
              campaign={`listen-${card.slug}`}
              className="btn-shop"
              showIcon={false}
              extra={{ song_title: card.title }}
            />
          ))
        ) : (
          <span className="font-body text-[0.68rem] text-ink-mute tracking-widest2 uppercase">
            Streaming soon
          </span>
        )}
      </div>
    </motion.div>
  );
}

export default function Listen() {
  const [filter, setFilter] = useState("all");
  const all = useMemo(() => getMusicCatalog(), []);
  const cards = filter === "all" ? all : all.filter((c) => c.tradition === filter);
  const recordings = getSongCount();

  useEffect(() => {
    trackEvent("view_content", {
      title_id: "listen",
      slug: "listen",
      surface: "listen",
      item_count: recordings,
    });
  }, [recordings]);

  return (
    <PageShell title="Listen">
      <PageHeader
        eyebrow={`${recordings} recordings · ${all.length} works`}
        title="Devotional and contemplative music."
        lede="Sanskrit, Pali, Hindi and Awadhi — set to mixes that travel from monastic to trance. Listen on the platform that's already on your phone."
      />
      <section className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="flex flex-wrap gap-2 mb-10">
          {FILTERS.map((f) => {
            const active = f.id === filter;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-full font-body text-[0.7rem] tracking-widest2 uppercase border transition-all duration-500 ease-breath ${
                  active
                    ? "bg-ink text-cream border-ink"
                    : "bg-transparent text-ink-soft border-dharma-gold/30 hover:border-dharma-gold hover:text-dharma-gold-deep"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 md:gap-6">
          {cards.map((c, i) => (
            <Card key={c.slug} card={c} index={i} />
          ))}
        </div>
      </section>
    </PageShell>
  );
}
